// libs
import * as React from "react";
import {
  Link,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  InputBase,
  Button,
  Stack,
} from "@mui/material";
import { getHeaders } from "../../utils/HeaderHelper";
import { AuthenticationContext } from "../../Context";
import DatePickerWithLabel from "../../common/DatePickerWithLabel";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import LoadingReportsTable from "../../shared/LoadingReportsTable";
import EmptyReportsTable from "../../shared/EmptyReportsTable";
import ReportAdvanceFilters from "../ReportAdvancedFilters";
import { checkPrivileges } from "../../utils/PrivilegesHelper";
import { PrivilegesContext } from "../../PrivilegesContext";
import BadgeList from "../../common/BadgeList";

interface Props {}

interface ChipData {
  key: number;
  label: string;
  type: string;
}

const EncounterInvoicesReport: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const userPrivileges = React.useContext(PrivilegesContext);

  // first mount  controller
  const isInitialMount = React.useRef(true);

  // other states
  const [customerOptions, setCustomerOptions] = React.useState<any>();
  const [selectedOrganizations, setSelectedOrganizations] = React.useState<
    any[]
  >([]);
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = React.useState<string>(null);
  const [endDate, setEndDate] = React.useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [lastName, setLastName] = React.useState<string>("");
  // data
  const [tableData, setTableData] = React.useState<any>([]);
  // filter options
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [pageLinks, setPageLinks] = React.useState<any>([]);
  const [paginatedList, setPaginatedList] = React.useState<any>([]);
  const perPage = 20;

  React.useEffect(() => {
    getData();
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [startDate, endDate, selectedOrganizations]);

  React.useEffect(() => {
    if (lastName.length > 1) {
      getData();
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [lastName]);

  React.useEffect(() => {
    getAssets();
  }, []);

  const getAssets = () => {
    fetch(`/admin/reports/encounter_detail/assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
        } else {
          setCustomerOptions(result?.resource?.customers);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getData = () => {
    if (!isInitialMount.current) {
      setLoading(true);
    }

    fetch(
      `/admin/reports/encounter_invoices/report_data?` +
        new URLSearchParams({
          start_date: startDate ? startDate : "",
          end_date: endDate ? endDate : "",
          last_name: lastName.length > 2 ? lastName : "",
          selected_organizations: selectedOrganizations?.join(",") || "",
        }),
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
        } else {
          setTableData(result?.resource?.results);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const paginateList = (list) => {
    if (list.length === 0) {
      return [];
    } else if (currentPage === 1) {
      return list.slice(0, perPage);
    } else {
      const offsetIndex = perPage * (currentPage - 1);
      return list.slice(offsetIndex, offsetIndex + perPage);
    }
  };

  const updatePagination = () => {
    setPaginatedList(paginateList(tableData));
  };

  const selectPage = (page) => {
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
  };

  React.useEffect(() => {
    if (tableData) {
      const pages = tableData.length / perPage;
      setPageCount(Math.ceil(pages));

      const links = [];
      for (let i = 1; i <= Math.ceil(pages); i++) {
        links.push(
          <Link
            key={i}
            onClick={() => selectPage(i)}
            className={currentPage === i ? "linkDark" : "linkLight"}
          >
            {i}
          </Link>
        );
      }
      setPageLinks(links);
      updatePagination();
    }
  }, [tableData, currentPage]);

  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteBadge = (badgeToDelete: ChipData) => {
    setSelectedOrganizations((prevSelected) =>
      prevSelected.filter((id) => id !== badgeToDelete.key)
    );
  };

  React.useEffect(() => {
    const orgIdToNameMap = customerOptions?.reduce((acc, org) => {
      acc[org.id] = org.name;
      return acc;
    }, {} as Record<string | number, string>);

    const badges = selectedOrganizations
      .map((orgId) => ({
        key: orgId,
        label: orgIdToNameMap?.[orgId] ?? `Organization ${orgId}`,
        type: "organization",
      }))
      .filter((badge) => badge.label);
    setBadgeData(badges);
  }, [selectedOrganizations, customerOptions]);

  return (
    <Box className="report-main-content">
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              href={`/diabetes-management-report`}
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontSize: "14px",
                }}
              />
            </Link>
            <Typography className="main-heading">
              Encounter Invoices Report
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{tableData.length}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>
            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={moment().subtract(1, "month").format("YYYY-MM-DD")}
              label="Encounter Start Date"
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={moment().format("YYYY-MM-DD")}
              label="Encounter End Date"
            />
            {checkPrivileges(userPrivileges, "Globalize NDPERS Reports") && (
              <div
                className="advanced-search-container"
                onClick={handleOpenFilter}
              >
                <FilterListIcon sx={{ width: 20, height: 20 }} />
                <p className="advanced-search-text">Advanced Search</p>
                {anchorEl ? (
                  <ArrowDropUp sx={{ width: 24, height: 24 }} />
                ) : (
                  <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
                )}
                <ReportAdvanceFilters
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  organizationOptions={customerOptions}
                  selectedOrganizations={selectedOrganizations}
                  setSelectedOrganizations={setSelectedOrganizations}
                />
              </div>
            )}
            <Button
              className="export-csv-button"
              onClick={() => {
                const params = new URLSearchParams({
                  start_date: startDate || "",
                  end_date: endDate || "",
                  last_name: lastName || "",
                  selected_organizations:
                    selectedOrganizations?.join(",") || "",
                }).toString();

                window.location.href = `/admin/reports/encounter_invoices/generate_csv.csv?${params}`;
              }}
            >
              Export as CSV
            </Button>
          </Box>
        </Box>

        <Box className="table-section">
          {badgeData?.length > 0 && (
            <div
              className="badge-container"
              style={{ width: "auto", marginBottom: "20px" }}
            >
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}
          {loading ? (
            <LoadingReportsTable isBadgeShowing={badgeData.length > 0} />
          ) : (
            <>
              {paginatedList?.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: badgeData.length > 0 ? "45vh" : "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow
                        sx={{
                          "& td, & th": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell align="left" sx={{ minWidth: "170px" }}>
                          Organization Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>
                          Organization NPI
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization City
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization State
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization Zip
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Place of Service
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient First Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient Last Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "125px" }}>
                          Patient MIddle Initial
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "125px" }}>
                          Provider
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Encounter Date
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient MRN #
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient DOB
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "94px" }}>
                          Patient Age
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient Gender
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Insurance ID #
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Program
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Initial or Follow-up
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedList?.map((report, index) => (
                        <TableRow
                          sx={{ "& td, & th": { borderBottom: "none" } }}
                          key={index + 1}
                        >
                          <TableCell align="left">
                            {report.customer_name}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_npi}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_city}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_state}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_zip}
                          </TableCell>
                          <TableCell align="left">
                            {report.place_of_service}
                          </TableCell>
                          <TableCell align="left">
                            {report.first_name}
                          </TableCell>
                          <TableCell align="left">{report.last_name}</TableCell>
                          <TableCell align="left">
                            {report.middle_name}
                          </TableCell>
                          <TableCell align="left">
                            {report.provider_name}
                          </TableCell>
                          <TableCell align="left">
                            {report.encounter_date}
                          </TableCell>
                          <TableCell align="left">
                            {report.mrn_number}
                          </TableCell>
                          <TableCell align="left">{report.dob}</TableCell>
                          <TableCell align="left">{report.age}</TableCell>
                          <TableCell align="left">{report.sex}</TableCell>
                          <TableCell align="left">
                            {report.insurance_id}
                          </TableCell>
                          <TableCell align="left">{report.program}</TableCell>
                          <TableCell align="left">
                            {report.appointment_type}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={badgeData.length > 0} />
              )}
            </>
          )}
          {paginatedList.length > 0 && (
            <p className="pagerLinks" style={{ marginBlockEnd: "0px" }}>
              <Link
                onClick={() => {
                  selectPage(currentPage > 1 ? currentPage - 1 : currentPage);
                }}
                className="linkDark"
              >
                &lt;
              </Link>
              {pageLinks}
              <Link
                onClick={() => {
                  selectPage(
                    currentPage < pageCount ? currentPage + 1 : currentPage
                  );
                }}
                className="linkDark"
              >
                &gt;
              </Link>
            </p>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EncounterInvoicesReport;
